import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Instagram } from "lucide-react";
import api, { naira } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { MaskedLines, Reveal } from "@/components/store/Reveal";
import { Marquee } from "@/components/store/Marquee";
import { ProductCard } from "@/components/store/ProductCard";

const IMG = {
  hero: "/api/static/brand/founder-hero.png",
  portrait: "/api/static/brand/philosophy-1.png",
  flatlay: "https://images.pexels.com/photos/6167444/pexels-photo-6167444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  jar: "https://images.pexels.com/photos/7038196/pexels-photo-7038196.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  texture: "https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxtaW5pbWFsJTIwc2tpbmNhcmUlMjBkcm9wcGVyJTIwYm90dGxlfGVufDB8fHx8MTc4NTA3MzQyM3ww&ixlib=rb-4.1.0&q=85",
};

const CHAPTERS = [
  { n: "01", t: "Educate first", d: "We teach proper routines and safe practices before we ever sell. Knowing your skin is the first step to loving it." },
  { n: "02", t: "Safe, clean formulas", d: "Every product is crafted with gentle, effective ingredients — no harsh shortcuts, no false promises." },
  { n: "03", t: "Real, lasting results", d: "From glow to even tone, our community's transformations speak louder than any advertisement." },
];

const CATEGORIES = [
  { name: "Face", img: "/api/static/products/glow_face_cream.jpg", to: "/shop?category=Face" },
  { name: "Body", img: "/api/static/products/natural_glow_lotion.jpg", to: "/shop?category=Body" },
  { name: "Kits", img: "/api/static/products/kit_complete.jpg", to: "/shop?category=Kits" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [reviews, setReviews] = useState([]);
  const { settings } = useStore();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 4 } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setReviews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[600px] overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img
            src={IMG.hero}
            alt="17/11 BeautyBarn founder holding products"
            className="w-full h-full object-cover"
            style={{ objectPosition: "60% 22%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-espresso/85 via-espresso/45 to-terracotta/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 via-transparent to-transparent" />
        </motion.div>

        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="overline text-bone/80 mb-5"
          >
            17/11 BeautyBarn — Port Harcourt
          </motion.p>
          <h1 className="font-serif text-bone text-[3rem] leading-[0.95] sm:text-7xl md:text-8xl tracking-tight">
            <MaskedLines lines={["Extracting", "beauty"]} delay={0.3} />
            <MaskedLines lines={["inside out."]} className="text-terracotta italic" delay={0.6} />
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="font-serif italic text-bone/75 text-lg md:text-xl mt-4"
          >
            — Glow that starts with knowledge.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="text-bone/85 max-w-lg mt-6 text-base md:text-lg"
          >
            Safe, effective skincare made for healthy, radiant skin — with the guidance to use it right.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="flex flex-wrap gap-4 mt-9"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-button"
              className="group inline-flex items-center gap-2 bg-terracotta text-bone px-7 py-3.5 rounded-full font-medium hover:bg-bone hover:text-espresso transition-colors"
            >
              Shop the collection
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/learn"
              data-testid="hero-learn-button"
              className="inline-flex items-center gap-2 border border-bone/50 text-bone px-7 py-3.5 rounded-full font-medium hover:bg-bone/10 transition-colors"
            >
              Learn skincare
            </Link>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-32" data-testid="featured-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <Reveal><p className="overline text-terracotta mb-3">Bestsellers</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl md:text-6xl text-espresso tracking-tight">Loved by our community</h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link to="/shop" className="inline-flex items-center gap-2 text-espresso font-medium hover:text-terracotta transition-colors">
              View all products <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-sand py-20 md:py-32" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <Reveal>
              <div className="relative overflow-hidden rounded-[2rem] bg-sand">
                <img
                  src={IMG.portrait}
                  alt="Founder philosophy"
                  className="w-full aspect-[3/4] object-cover"
                  style={{ objectPosition: "50% 15%" }}
                />
                {/* warm tone wash to harmonise studio light with the palette */}
                <div className="absolute inset-0 bg-terracotta/20 mix-blend-multiply" />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/25 via-transparent to-transparent" />
                {/* soft vignette + bottom fade so the photo melts into the sand section */}
                <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 90px 34px #F4EFE6" }} />
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-sand to-transparent" />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <Reveal><p className="overline text-terracotta mb-4">Our philosophy</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl md:text-5xl text-espresso tracking-tight mb-10 leading-tight">
                Beautiful skin is built on trust, not trends.
              </h2>
            </Reveal>
            <div className="space-y-8">
              {CHAPTERS.map((c, i) => (
                <Reveal key={c.n} delay={0.15 + i * 0.1}>
                  <div className="flex gap-6 border-t border-sandline pt-6">
                    <span className="font-serif text-3xl text-terracotta">{c.n}</span>
                    <div>
                      <h3 className="text-espresso text-xl font-serif mb-1">{c.t}</h3>
                      <p className="text-bark leading-relaxed">{c.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-32" data-testid="categories-section">
        <Reveal><p className="overline text-terracotta mb-3">Shop by need</p></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-4xl md:text-6xl text-espresso tracking-tight mb-12">Find your ritual</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-5">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.1}>
              <Link to={c.to} className="group relative block overflow-hidden rounded-2xl aspect-[4/5]" data-testid={`category-${c.name}`}>
                <img src={c.img} alt={c.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 to-transparent" />
                <div className="absolute bottom-0 left-0 p-7 flex items-center justify-between w-full">
                  <span className="font-serif text-3xl text-bone">{c.name}</span>
                  <ArrowUpRight className="w-6 h-6 text-bone group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="bg-espresso text-bone py-20 md:py-32" data-testid="home-reviews">
          <div className="max-w-7xl mx-auto px-5 md:px-10">
            <Reveal><p className="overline text-terracotta mb-3">Real results</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-14">What our customers say</h2>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-8">
              {reviews.map((r, i) => (
                <Reveal key={r.id} delay={i * 0.1}>
                  <div className="border-t border-white/15 pt-6">
                    <div className="text-terracotta mb-4">{"★".repeat(r.rating || 5)}</div>
                    <p className="font-serif text-2xl leading-snug mb-6">“{r.text}”</p>
                    <p className="overline text-bone/60">{r.name}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <Link to="/reviews" className="inline-flex items-center gap-2 mt-12 text-bone hover:text-terracotta transition-colors font-medium">
                See all reviews <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      {/* SOCIAL CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-32 text-center" data-testid="social-cta">
        <Reveal>
          <p className="overline text-terracotta mb-4">Join the community</p>
          <h2 className="font-serif text-4xl md:text-7xl text-espresso tracking-tight max-w-4xl mx-auto leading-tight">
            100K+ glowing up with us on TikTok &amp; Instagram.
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
            <a
              href={settings.instagram || "#"}
              target="_blank"
              rel="noreferrer"
              data-testid="social-instagram"
              className="inline-flex items-center gap-2 border border-espresso text-espresso px-7 py-4 rounded-full font-medium hover:bg-espresso hover:text-bone transition-colors"
            >
              <Instagram className="w-4 h-4" /> Instagram
            </a>
            <a
              href={settings.tiktok || "#"}
              target="_blank"
              rel="noreferrer"
              data-testid="social-tiktok"
              className="inline-flex items-center gap-2 border border-espresso text-espresso px-7 py-4 rounded-full font-medium hover:bg-espresso hover:text-bone transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
              TikTok
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-espresso text-bone px-8 py-4 rounded-full font-medium hover:bg-terracotta transition-colors"
              data-testid="social-cta-button"
            >
              Say hello <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
